'use client'

/**
 * ChromeCondense: the sticky header gives back height once the reader scrolls
 * (site audit 2026-10-21, item 2.10: a 64px bar is about 8% of a 390px phone
 * viewport, forever).
 *
 * It renders nothing. After mount it watches the scroll position and sets
 * `data-v3-chrome-condensed` on <html> past CONDENSE_AT, clearing it again
 * under EXPAND_AT; the gap between the two keeps the bar from flickering at
 * the threshold. ChromeCondense.css shortens the bar (never under a 44px tap
 * target). Nothing is read during render, so the server HTML and the first
 * client render are the same full-height bar (ci:hydration-safety).
 *
 * The --v3-chrome-h token is NOT changed: hero heights and sticky offsets
 * across the site are computed from it, and changing it mid-scroll would make
 * the page under the bar jump.
 */
import { useEffect } from 'react'
import { chromeCondensedNext } from '@/lib/site/chrome-condense'
import './ChromeCondense.css'

const CHROME_CONDENSED_ATTR = 'data-v3-chrome-condensed'

export function ChromeCondense() {
  useEffect(() => {
    const root = document.documentElement
    let condensed = root.hasAttribute(CHROME_CONDENSED_ATTR)
    let frame = 0
    const apply = () => {
      frame = 0
      const next = chromeCondensedNext(condensed, window.scrollY)
      if (next === condensed) return
      condensed = next
      if (next) root.setAttribute(CHROME_CONDENSED_ATTR, '')
      else root.removeAttribute(CHROME_CONDENSED_ATTR)
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(apply)
    }
    apply()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
      root.removeAttribute(CHROME_CONDENSED_ATTR)
    }
  }, [])
  return null
}
